import { Progress } from "@/common/components/ui/progress";
import { SectionTitle } from "../utils/helpers.jsx";

export default function CourseProgress({ courses, isTableView = false }) {
  // Table view format
  if (isTableView) {
    // Get status band based on progress percentage
    const getProgressBand = (percentage) => {
      if (percentage === 100) return "Completed";
      if (percentage >= 75) return "Advanced";
      if (percentage >= 50) return "Intermediate";
      if (percentage >= 25) return "Beginner";
      return "Just Started";
    };

    // Get band class for styling
    const getBandClass = (percentage) => {
      if (percentage === 100) return "bg-green-100 text-green-800";
      if (percentage >= 75) return "bg-blue-100 text-blue-800";
      if (percentage >= 50) return "bg-purple-100 text-purple-800";
      if (percentage >= 25) return "bg-yellow-100 text-yellow-800";
      return "bg-red-100 text-red-800";
    };

    return (
      <div className="mb-8">
        <SectionTitle title="📚 Course Progress" />
        {courses.length > 0 ? (
          <div className="overflow-x-auto print:overflow-visible">
            <table className="w-full border border-collapse border-gray-200 print:border-collapse">
              <thead>
                <tr className="bg-gray-50 print:bg-gray-100">
                  <th className="px-4 py-2 font-medium text-left border text-slate-600 print:font-bold print:text-black">
                    Course Name
                  </th>
                  <th className="px-4 py-2 font-medium text-center border text-slate-600 print:font-bold print:text-black">
                    Total Modules
                  </th>
                  <th className="px-4 py-2 font-medium text-center border text-slate-600 print:font-bold print:text-black">
                    Completed
                  </th>
                  <th className="px-4 py-2 font-medium text-center border text-slate-600 print:font-bold print:text-black">
                    Progress
                  </th>
                  <th className="px-4 py-2 font-medium text-left border text-slate-600 print:font-bold print:text-black">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {courses.map((course) => (
                  <tr key={course.id}>
                    <td className="px-4 py-3 font-medium border">
                      {course.name}
                    </td>
                    <td className="px-4 py-3 text-center border">
                      {course.total}
                    </td>
                    <td className="px-4 py-3 text-center border">
                      {course.completed}
                    </td>
                    <td className="px-4 py-3 border">
                      <div className="flex items-center gap-2">
                        <div className="w-full h-3 bg-gray-200 rounded-full print:h-2">
                          <div
                            className={`h-3 rounded-full print:h-2 ${
                              course.progress < 30
                                ? "bg-red-500"
                                : course.progress < 70
                                ? "bg-yellow-500"
                                : "bg-green-500"
                            }`}
                            style={{ width: `${course.progress}%` }}
                          ></div>
                        </div>
                        <span className="text-xs font-medium print:font-bold">
                          {course.progress}%
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 border">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${getBandClass(
                          course.progress
                        )} print:font-bold`}
                      >
                        {getProgressBand(course.progress)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4 text-center text-gray-500 border border-gray-200 rounded-lg">
            No courses available
          </div>
        )}
      </div>
    );
  }

  // Original card view
  return (
    <div className="mb-8">
      <SectionTitle title="Course Progress" />
      {courses.length > 0 ? (
        <div className="space-y-4">
          {courses.map((course) => (
            <div
              key={course.id}
              className="p-3 border border-gray-200 rounded-lg shadow-sm md:p-4"
            >
              <div className="space-y-2 md:space-y-3">
                <div className="flex flex-col justify-between gap-2 md:flex-row md:items-center">
                  <h3 className="text-base font-semibold md:text-lg">
                    {course.name}
                  </h3>
                  <div className="flex gap-6">
                    <div>
                      <p className="text-xs md:text-sm text-slate-500">Total</p>
                      <p className="text-lg font-bold md:text-xl text-slate-900">
                        {course.total}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs md:text-sm text-slate-500">
                        Completed
                      </p>
                      <p className="text-lg font-bold text-blue-600 md:text-xl">
                        {course.completed}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between text-xs md:text-sm">
                    <span>Progress</span>
                    <span className="font-medium">{course.progress}%</span>
                  </div>
                  <Progress
                    value={course.progress}
                    className="h-2"
                    style={{
                      backgroundColor: "#e2e8f0",
                      "--progress-color":
                        course.progress < 30
                          ? "#ef4444"
                          : course.progress < 70
                          ? "#f59e0b"
                          : "#22c55e",
                      "--progress-value": `${course.progress}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-4 text-center text-gray-500">
          No courses available
        </div>
      )}
    </div>
  );
}
